"use client";

import { useState, Suspense, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Link from "next/link";
import toast from "react-hot-toast";
import {
  loginSchema,
  signUpSchema,
  type LoginFormData,
  type SignUpFormData,
} from "../../../lib/schemas";
import { signUp, checkUsernameAvailability } from "../../../lib/auth";
import { signIn as nextAuthSignIn } from "next-auth/react";
import { Button } from "../../../components/ui/Button";
import { Input } from "../../../components/ui/Input";
import { Select } from "../../../components/ui/Select";
import { Checkbox } from "../../../components/ui/Checkbox";
import { KOREAN_REGIONS } from "../../../lib/utils";
import { getPasswordStrength } from "../../../lib/utils/passwordValidation";
import { getUserProfile } from "../../../lib/profile/api";
import {
  loginWithKakao,
  loginWithNaver,
  handleRedirectResult,
} from "../../../lib/auth/snsAuth";
import SnsAuthButtons, {
  type SnsProvider,
} from "@/components/auth/SnsAuthButtons";
import { guardGoogleOAuthInAppBrowser } from "@/components/auth/InAppBrowserGoogleBanner";
import {
  scheduleSnsLoadingFallback,
  useSnsOAuthLoadingRecovery,
} from "@/lib/hooks/useSnsOAuthLoadingRecovery";

function LoginPageContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [usernameChecked, setUsernameChecked] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(
    null
  );
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [usernameError, setUsernameError] = useState("");
  const [loginError, setLoginError] = useState("");
  const [signUpError, setSignUpError] = useState("");
  const [snsLoading, setSnsLoading] = useState<SnsProvider>(null);

  useSnsOAuthLoadingRecovery(setSnsLoading);

  const navigateAfterAuth = async (uid: string) => {
    const callbackUrl = searchParams.get("callbackUrl") || "/";
    const profile = await getUserProfile(uid);
    const completed = profile.success && profile.data?.onboardingCompleted;

    const { getFirebaseDb } = await import(
      "../../../lib/api/firebase-ultra-safe"
    );
    const db = await getFirebaseDb();
    const { doc, getDoc } = await import("firebase/firestore");
    const instructorDoc = await getDoc(doc(db, "instructors", uid));
    const isInstructor = instructorDoc.exists();

    const nextUrl =
      isInstructor || completed
        ? callbackUrl
        : `/onboarding?next=${encodeURIComponent(callbackUrl)}`;

    router.push(nextUrl);
  };

  useEffect(() => {
    let cancelled = false;
    const timerId = window.setTimeout(() => {
      const checkRedirectResult = async () => {
        try {
          const result = await handleRedirectResult();
          if (!cancelled && result) {
            await navigateAfterAuth(result.uid);
          }
        } catch (error) {
          console.log("리다이렉트 결과 확인:", error);
        }
      };
      checkRedirectResult();
    }, 0);

    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
    };
  }, [router, searchParams]);

  const loginForm = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const signUpForm = useForm<SignUpFormData>({
    resolver: zodResolver(signUpSchema),
  });

  const password = signUpForm.watch("password", "");
  const username = signUpForm.watch("username", "");

  const handleCheckUsername = async () => {
    if (!username || username.length < 4) {
      toast.error("아이디를 4자 이상 입력해주세요.");
      return;
    }

    setIsCheckingUsername(true);
    try {
      const isAvailable = await checkUsernameAvailability(username);
      setUsernameAvailable(isAvailable);
      setUsernameChecked(true);

      if (isAvailable) {
        toast.success("사용 가능한 아이디입니다!");
      } else {
        toast.error("이미 사용 중인 아이디입니다.");
      }
    } catch (error) {
      toast.error("아이디 중복확인 중 오류가 발생했습니다.");
      setUsernameAvailable(null);
      setUsernameChecked(false);
    } finally {
      setIsCheckingUsername(false);
    }
  };

  const onLoginSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setLoginError(""); // 오류 메시지 초기화
    try {
      console.log("🔐 로그인 시도 시작:", data.username);

      // 클라이언트사이드 Firebase Auth 사용
      const { signInWithEmailAndPassword } = await import("firebase/auth");
      const { getFirebaseAuth } = await import(
        "../../../lib/api/firebase-ultra-safe"
      );

      const auth = getFirebaseAuth();
      if (!auth) {
        throw new Error("Firebase Auth initialization failed");
      }

      // 사용자명을 이메일로 변환 (Firebase Auth는 이메일로만 로그인 가능)
      // 사용자명이 이미 이메일인 경우 그대로 사용, 아니면 @connectone.local 추가
      const email = data.username.includes("@")
        ? data.username
        : `${data.username}@connectone.local`;

      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        data.password
      );

      if (userCredential.user) {
        console.log("✅ 로그인 성공!");
        // toast.success("로그인 성공!"); // 메시지 제거

        // 이전 페이지로 리다이렉트 (callbackUrl 파라미터 확인)
        const callbackUrl = searchParams.get("callbackUrl") || "/";

        // 온보딩 완료 여부 및 프로 여부 확인
        const profile = await getUserProfile(userCredential.user.uid);
        const completed = profile.success && profile.data?.onboardingCompleted;
        
        // 프로 여부 확인 (instructors 컬렉션에 문서 존재 여부)
        const { getFirebaseDb } = await import("../../../lib/api/firebase-ultra-safe");
        const db = await getFirebaseDb();
        const { doc, getDoc } = await import("firebase/firestore");
        const instructorDoc = await getDoc(doc(db, "instructors", userCredential.user.uid));
        const isInstructor = instructorDoc.exists();
        
        // 프로인 경우 온보딩 스킵, 일반 회원만 온보딩 진행
        const nextUrl = isInstructor || completed
          ? callbackUrl
          : `/onboarding?next=${encodeURIComponent(callbackUrl)}`;

        console.log("🔄 리다이렉트:", nextUrl, "온보딩완료:", completed, "프로여부:", isInstructor);
        router.push(nextUrl);
      }
    } catch (error: unknown) {
      console.error("❌ 로그인 실패:", error);

      const code =
        typeof error === "object" && error && "code" in error
          ? String((error as { code?: string }).code)
          : undefined;

      if (code === "auth/user-not-found") {
        setLoginError("등록되지 않은 이메일입니다.");
      } else if (code === "auth/wrong-password") {
        setLoginError("비밀번호가 올바르지 않습니다.");
      } else if (code === "auth/invalid-email") {
        setLoginError("올바르지 않은 이메일 형식입니다.");
      } else if (code === "auth/too-many-requests") {
        setLoginError(
          "너무 많은 시도가 있었습니다. 잠시 후 다시 시도해주세요."
        );
      } else {
        setLoginError("로그인 중 오류가 발생했습니다.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onSignUpSubmit = async (data: SignUpFormData) => {
    // 아이디 중복확인 체크
    if (!usernameChecked || !usernameAvailable) {
      toast.error("아이디 중복확인을 완료해주세요.");
      return;
    }
    setIsLoading(true);
    setSignUpError(""); // 오류 메시지 초기화
    try {
      await signUp({
        username: data.username,
        email: data.email,
        password: data.password,
        nickname: data.nickname,
        region: data.region,
        agreeTerms: data.agreeTerms,
      });

      // toast.success("회원가입이 완료되었습니다!"); // 메시지 제거
      setIsSignUp(false);
      // 폼 초기화
      signUpForm.reset();
      setUsernameChecked(false);
      setUsernameAvailable(null);
    } catch (error) {
      setSignUpError(
        error instanceof Error
          ? error.message
          : "회원가입 중 오류가 발생했습니다."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSNSLogin = async (provider: "google" | "kakao" | "naver") => {
    if (provider === "google" && (await guardGoogleOAuthInAppBrowser())) {
      return;
    }

    setSnsLoading(provider);
    const clearSnsLoading = () => setSnsLoading(null);
    const cancelFallback = scheduleSnsLoadingFallback(clearSnsLoading);

    try {
      if (provider === "google") {
        const callbackUrl = searchParams.get("callbackUrl") || "/";
        await nextAuthSignIn("google", {
          callbackUrl,
          redirect: true,
        });
        return;
      }

      if (provider === "kakao") {
        const callbackUrl = searchParams.get("callbackUrl") || "/";
        await loginWithKakao(callbackUrl);
        return;
      }

      if (provider === "naver") {
        const callbackUrl = searchParams.get("callbackUrl") || "/";
        await loginWithNaver(callbackUrl);
        return;
      }
    } catch (error: unknown) {
      console.error(`${provider} 로그인 오류:`, error);
      const providerName =
        provider === "google"
          ? "구글"
          : provider === "kakao"
            ? "카카오"
            : "네이버";

      const message =
        error instanceof Error
          ? error.message
          : `${providerName} 로그인 중 오류가 발생했습니다.`;

      if (
        provider === "google" &&
        error instanceof Error &&
        (error.message.includes("Configuration") ||
          error.message.includes("Provider"))
      ) {
        toast.error(
          "구글 로그인 설정이 완료되지 않았습니다. 환경변수를 확인해주세요.",
          { duration: 5000 }
        );
      } else {
        toast.error(message);
      }
    } finally {
      cancelFallback();
      clearSnsLoading();
    }
  };

  const handleClose = () => {
    if (isLoading) return;
    setSnsLoading(null);
    router.replace("/");
  };

  return (
    <div
      className="fixed inset-0 z-[10000] overflow-y-auto overscroll-y-contain bg-black/50 backdrop-blur-sm"
      onClick={handleClose}
    >
      <div className="flex min-h-full min-h-[100dvh] items-start justify-center px-4 py-6 pb-10 sm:items-center sm:py-8">
        <div
          className="w-full max-w-md shrink-0 rounded-3xl bg-white shadow-2xl animate-in fade-in zoom-in-95 duration-300"
          onClick={event => event.stopPropagation()}
        >
        {/* 웰컴 보너스 배너 */}
        <div className="bg-violet-600 text-white text-center py-3 px-4 rounded-t-3xl">
          <p className="text-sm font-medium">
            신규 회원가입 시 <span className="font-semibold">5,000P</span> 즉시 지급
          </p>
        </div>

        {/* 모바일 스타일 헤더 - 팝업에서는 닫기 버튼으로 변경하거나 심플하게 유지 */}
        <div className="bg-white text-gray-900 px-6 py-4 flex items-center justify-between border-b border-gray-100">
          <h1 className="text-xl font-bold">
            {isSignUp ? "회원가입" : "로그인"}
          </h1>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="닫기"
          >
            <svg
              className="w-6 h-6 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* 로그인/회원가입 폼 */}
        <div className="px-6 py-5 pb-6">
          <div className="w-full">
          {/* 로그인 폼 */}
          {!isSignUp ? (
            <>
            <form
              className="space-y-4"
              onSubmit={loginForm.handleSubmit(onLoginSubmit)}
            >
              {/* 아이디 입력 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  아이디
                </label>
                <input
                  type="text"
                  autoComplete="username"
                  placeholder="아이디를 입력해주세요"
                  className="w-full px-3 py-3 border-b border-gray-300 text-gray-900 placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                  {...loginForm.register("username")}
                />
                {loginForm.formState.errors.username && (
                  <p className="mt-1 text-sm text-red-500">
                    {loginForm.formState.errors.username.message}
                  </p>
                )}
              </div>

              {/* 비밀번호 입력 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  비밀번호
                </label>
                <input
                  type="password"
                  autoComplete="current-password"
                  placeholder="비밀번호를 입력해주세요"
                  className="w-full px-3 py-3 border-b border-gray-300 text-gray-900 placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                  {...loginForm.register("password")}
                />
                {loginForm.formState.errors.password && (
                  <p className="mt-1 text-sm text-red-500">
                    {loginForm.formState.errors.password.message}
                  </p>
                )}
              </div>

              {/* 자동 로그인 체크박스 */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="autoLogin"
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                />
                <label
                  htmlFor="autoLogin"
                  className="ml-2 text-sm text-gray-700"
                >
                  자동 로그인
                </label>
              </div>

              {/* 로그인 오류 메시지 */}
              {loginError && (
                <div className="text-red-600 text-sm text-center py-2">
                  {loginError}
                </div>
              )}

              {/* 로그인 버튼 */}
              <Button
                type="submit"
                className="w-full bg-violet-600 hover:bg-violet-700 text-white font-semibold py-3.5 rounded-xl transition-colors"
                loading={isLoading}
                disabled={isLoading}
              >
                {isLoading ? "로그인 중..." : "로그인"}
              </Button>
            </form>

            <p className="mt-4 text-center text-sm text-gray-600">
              계정이 없으신가요?{" "}
              <Link
                href="/auth/signup"
                className="font-semibold text-violet-600 hover:text-violet-500"
              >
                회원가입하기
              </Link>
            </p>

            <div className="mt-5">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">또는</span>
                </div>
              </div>

              <SnsAuthButtons
                mode="login"
                layout="icons"
                snsLoading={snsLoading}
                onGoogle={() => handleSNSLogin("google")}
                onKakao={() => handleSNSLogin("kakao")}
                onNaver={() => handleSNSLogin("naver")}
                className="mt-4"
              />
            </div>

            <div className="mt-5 flex justify-center space-x-4 text-sm text-gray-600">
              <Link href="/auth/find-email" className="hover:text-blue-600">
                아이디 찾기
              </Link>
              <span className="text-gray-300">|</span>
              <Link href="/auth/reset-password" className="hover:text-blue-600">
                비밀번호 찾기
              </Link>
            </div>
            </>
          ) : (
            /* 회원가입 폼 */
            <form
              className="space-y-6"
              onSubmit={signUpForm.handleSubmit(onSignUpSubmit)}
            >
              <div className="space-y-4">
                <SnsAuthButtons
                  mode="signup"
                  layout="icons"
                  snsLoading={snsLoading}
                  onGoogle={() => handleSNSLogin("google")}
                  onKakao={() => handleSNSLogin("kakao")}
                  onNaver={() => handleSNSLogin("naver")}
                />
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="px-2 bg-white text-gray-500">
                      또는 이메일로 가입
                    </span>
                  </div>
                </div>
              </div>

              {/* 아이디 입력 필드 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  아이디
                </label>
                <div className="flex space-x-2">
                  <div className="flex-1">
                    <input
                      type="text"
                      autoComplete="username"
                      placeholder="4-20자, 영어와 숫자만 사용 가능"
                      className={`w-full px-3 py-3 border-b border-gray-300 text-gray-900 placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors ${
                        signUpForm.formState.errors.username
                          ? "border-red-500"
                          : "border-gray-300"
                      } ${
                        usernameChecked && usernameAvailable
                          ? "border-green-500"
                          : ""
                      } ${
                        usernameChecked && !usernameAvailable
                          ? "border-red-500"
                          : ""
                      }`}
                      {...signUpForm.register("username")}
                      onChange={e => {
                        signUpForm.register("username").onChange(e);
                        setUsernameChecked(false);
                        setUsernameAvailable(null);
                      }}
                      onKeyDown={e => {
                        if (
                          e.key === "Process" ||
                          /[ㄱ-ㅎ|ㅏ-ㅣ|가-힣]/.test(e.key)
                        ) {
                          e.preventDefault();
                          setUsernameError(
                            "아이디는 영어와 숫자만 사용할 수 있습니다."
                          );
                          setTimeout(() => setUsernameError(""), 3000);
                        }
                      }}
                      onInput={e => {
                        const target = e.target as HTMLInputElement;
                        const value = target.value.replace(/[^a-zA-Z0-9]/g, "");
                        if (target.value !== value) {
                          target.value = value;
                          signUpForm.register("username").onChange({ target });
                          setUsernameError(
                            "아이디는 영어와 숫자만 사용할 수 있습니다."
                          );
                          setTimeout(() => setUsernameError(""), 3000);
                        }
                      }}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCheckUsername}
                    loading={isCheckingUsername}
                    disabled={
                      !username || username.length < 4 || isCheckingUsername
                    }
                    className="px-4 py-3 whitespace-nowrap"
                  >
                    {isCheckingUsername ? "확인중..." : "중복확인"}
                  </Button>
                </div>
                {signUpForm.formState.errors.username && (
                  <p className="mt-1 text-sm text-red-600">
                    {signUpForm.formState.errors.username.message}
                  </p>
                )}
                {usernameError && (
                  <p className="mt-1 text-sm text-red-600">{usernameError}</p>
                )}
                {usernameChecked && usernameAvailable && (
                  <p className="mt-1 text-sm text-green-600">
                    ✓ 사용 가능한 아이디입니다
                  </p>
                )}
                {usernameChecked && !usernameAvailable && (
                  <p className="mt-1 text-sm text-red-600">
                    ✗ 이미 사용 중인 아이디입니다
                  </p>
                )}
                {!usernameChecked && !usernameError && (
                  <p className="mt-1 text-sm text-gray-500">
                    영어(대소문자)와 숫자만 사용할 수 있습니다
                  </p>
                )}
              </div>

              {/* 이메일 입력 필드 */}
              <Input
                label="이메일"
                type="email"
                autoComplete="email"
                placeholder="이메일을 입력해주세요"
                error={signUpForm.formState.errors.email?.message}
                helperText="실제 이메일 주소를 입력해주세요"
                {...signUpForm.register("email")}
              />

              {/* 비밀번호 입력 필드 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  비밀번호
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    placeholder="8자 이상, 영문+숫자+특수문자 포함"
                    className={`w-full px-3 py-3 pr-10 border-b border-gray-300 text-gray-900 placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors ${
                      signUpForm.formState.errors.password
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    {...signUpForm.register("password")}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? (
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    )}
                  </button>
                </div>
                {signUpForm.formState.errors.password && (
                  <p className="mt-1 text-sm text-red-600">
                    {signUpForm.formState.errors.password.message}
                  </p>
                )}
                {password && (
                  <div className="mt-2">
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${
                            getPasswordStrength(password).score === 0
                              ? "bg-red-500 w-0"
                              : getPasswordStrength(password).score === 1
                                ? "bg-red-500 w-1/4"
                                : getPasswordStrength(password).score === 2
                                  ? "bg-orange-500 w-2/4"
                                  : getPasswordStrength(password).score === 3
                                    ? "bg-yellow-500 w-3/4"
                                    : getPasswordStrength(password).score === 4
                                      ? "bg-blue-500 w-full"
                                      : "bg-green-500 w-full"
                          }`}
                        />
                      </div>
                      <span
                        className={`text-sm font-medium ${getPasswordStrength(password).color}`}
                      >
                        {getPasswordStrength(password).label}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* 비밀번호 확인 입력 필드 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  비밀번호 확인
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    autoComplete="new-password"
                    placeholder="비밀번호를 다시 입력해주세요"
                    className={`w-full px-3 py-3 pr-10 border-b border-gray-300 text-gray-900 placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors ${
                      signUpForm.formState.errors.confirmPassword
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    {...signUpForm.register("confirmPassword")}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showConfirmPassword ? (
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    )}
                  </button>
                </div>
                {signUpForm.formState.errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">
                    {signUpForm.formState.errors.confirmPassword.message}
                  </p>
                )}
              </div>

              <Input
                label="닉네임"
                type="text"
                autoComplete="nickname"
                placeholder="닉네임을 입력해주세요"
                error={signUpForm.formState.errors.nickname?.message}
                helperText="한글, 영문, 숫자만 사용 가능합니다"
                {...signUpForm.register("nickname")}
              />

              <Select
                label="지역"
                placeholder="지역을 선택해주세요"
                options={KOREAN_REGIONS}
                error={signUpForm.formState.errors.region?.message}
                {...signUpForm.register("region")}
              />

              <Checkbox
                label="서비스 이용약관 및 개인정보처리방침에 동의합니다"
                error={signUpForm.formState.errors.agreeTerms?.message}
                {...signUpForm.register("agreeTerms")}
              />

              {/* 회원가입 오류 메시지 */}
              {signUpError && (
                <div className="text-red-600 text-sm text-center py-2">
                  {signUpError}
                </div>
              )}

              {/* 회원가입 버튼 */}
              <Button
                type="submit"
                className="w-full bg-violet-600 hover:bg-violet-700 text-white font-semibold py-3.5 rounded-xl transition-colors"
                loading={isLoading}
                disabled={isLoading}
              >
                {isLoading ? "회원가입 중..." : "회원가입"}
              </Button>
            </form>
          )}

          {/* 회원가입 탭 하단 링크 */}
          {isSignUp && (
          <div className="mt-6 space-y-4">
            <div className="text-center">
              <button
                onClick={() => {
                  setIsSignUp(false);
                  loginForm.reset();
                }}
                className="text-blue-600 hover:text-blue-500 font-medium"
              >
                로그인하기
              </button>
            </div>
          </div>
          )}
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <LoginPageContent />
    </Suspense>
  );
}

